import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/defaultV2.min.css';
import { applyQuillHtmlDisplay, bindQuillHtmlDisplay } from '../lib/quillHtmlDisplay';
import { DISMISSAL_FORM_ID, DISMISSAL_FORM_TITLE } from '../lib/dismissalForm';
import { locksOpenL2 } from '../lib/portalRoles';
import { TextareaMicOverlay } from '../components/TextareaMicOverlay';
import { bindSurvey, executeValidateThenSubmit, resetFafBindings, } from '../faf/fafHost';
import { fetchFormDefinition } from '../services/workflow';
import { referralStatusLabel } from '../lib/referralStatus';
import { incidentLevelLabel, CLOSED_STATUS, isClosedIncidentStatus, isEscalationLevel, NEW_STATUS, } from '../lib/incidentLevels';
import { OPEN_STATUS } from '../lib/referralTimeLimits';
import { isUploadTooLarge, UPLOAD_TOO_LARGE_MESSAGE } from '../lib/uploadLimits';
import { parseReferralUpdateDescription, prependReferralUpdateType, } from '../lib/referralUpdateTypes';
import { extractMentionTags, splitMentionParts } from '../lib/updateMentions';
import { UpdateMentionTextarea } from '../components/UpdateMentionTextarea';
import { getUnseenReferralUpdateIds, markReferralUpdatesSeen } from '../lib/seenReferralUpdates';
import { SiteUserPicker } from '../components/SiteUserPicker';
import { sortLookupUsers } from '../lib/siteUsers';
import { collectTaskAssignees, userIdFromAdditionalFields, withPersonUserId, } from '../lib/taskAssignees';
import { isReferralTaskDone, parseTaskUpdateDescription, referralTaskStatusLabel, } from '../lib/referralTaskStatus';
import { defaultLookupValue, ensureIncidentLookup, namedValueLabel, } from '../services/incidentLookup';
import { completeReferralTask, copyReferralItemsFromParent, createReferralTask, createReferralTaskUpdatedInfo, createReferralUpdatedInfo, deleteReferralTask, fetchAttachmentDownload, fetchReferralDetail, replaceReferralPersons, requestL15, closeReferral, dismissReferral, saveBase64File, updateReferralSite, } from '../services/referrals';
import { collectParentCopyForms } from '../lib/copyFromParentL1';
import { ensureSignInUserInfo } from '../services/signinUserInfo';
import { uploadReferralAttachment } from '../services/workflow';
import { uploadBlobToS3 } from '../services/s3Upload';
function formatDateTime(value) {
    if (value == null || value === '')
        return '—';
    const raw = String(value);
    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z';
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime()))
        return raw;
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}
function userName(user) {
    if (!user)
        return '—';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || user.email || '—';
}
function personDisplayName(person) {
    const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
    return name || person.email || `Person ${person.id}`;
}
function isDataSheetRelation(relation) {
    return (relation.level === 'Data Sheet' || /data\s*sheet/i.test(relation.issueType));
}
function relatedIncidentSummary(relation, role, statuses) {
    const level = incidentLevelLabel(relation.level);
    const peopleLabel = relation.personCount === 1
        ? '1 person involved'
        : `${relation.personCount} people involved`;
    return (_jsxs(_Fragment, { children: [_jsxs("span", { children: [_jsxs("strong", { children: [role, " ", level !== '—' ? level : 'incident', " #", relation.id] }), relation.referId != null ? _jsxs("span", { children: ["Case ID ", relation.referId] }) : null, _jsx("span", { children: relation.issueType || 'No incident type' }), relation.siteName ? _jsx("span", { children: relation.siteName }) : null, _jsx("span", { children: peopleLabel })] }), _jsxs("span", { children: [referralStatusLabel(relation.status, statuses), ' · ', formatDateTime(relation.created)] })] }));
}
/**
 * Rows created before a lookup changed can carry values this environment no
 * longer defines (e.g. person type 4). A <select> can't represent those — it
 * silently displays its first option while the draft keeps the stale value — so
 * snap them to a real one on load instead.
 */
function normalizeDraftValue(value, values) {
    if (values.length === 0)
        return value;
    return values.some((entry) => entry.value === value) ? value : defaultLookupValue(values);
}
function toPersonDraft(person) {
    return {
        id: person.id,
        type: person.type,
        kind: person.kind,
        firstName: person.firstName,
        lastName: person.lastName,
        email: person.email === 'test@kokomo247.com' ? '' : person.email ?? '',
        program: person.program,
        additionalFields: person.additionalFields ?? null,
        sourceUserId: userIdFromAdditionalFields(person.additionalFields) ?? person.userId ?? null,
    };
}
function surveyTitle(survey) {
    return typeof survey?.title === 'string' ? survey.title : '';
}
function answeredFieldCount(formData) {
    if (!formData)
        return 0;
    const seen = new Set();
    for (const [key, value] of Object.entries(formData)) {
        if (value == null || value === '' || (Array.isArray(value) && value.length === 0))
            continue;
        seen.add(key.includes('.') ? key.slice(key.lastIndexOf('.') + 1) : key);
    }
    return seen.size;
}
function formatFileSize(bytes) {
    if (bytes == null || !Number.isFinite(bytes) || bytes < 0)
        return '';
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function canDownloadAttachment(attachment) {
    return Boolean(attachment.downloadUrl?.trim());
}
function pickFormString(data, ...keys) {
    for (const key of keys) {
        const value = data[key];
        if (typeof value === 'string' && value.trim())
            return value.trim();
        if (typeof value === 'number' && Number.isFinite(value))
            return String(value);
    }
    return '';
}
function patientNameFromFormData(data) {
    const nested = data.patientName;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        const record = nested;
        const name = [record.firstName, record.lastName]
            .map((part) => (typeof part === 'string' ? part.trim() : ''))
            .filter(Boolean)
            .join(' ');
        if (name)
            return name;
    }
    return pickFormString(data, 'patientFullName', 'patient_name');
}
function isPsychPlusReferralResponse(form) {
    const title = `${form.formTitle ?? ''} ${surveyTitle(form.survey)}`.toLowerCase();
    if (title.includes('psychplus') && title.includes('referral'))
        return true;
    const data = form.formData ?? {};
    return ((typeof data.contactStatus === 'string' || typeof data.initialAppointmentId === 'number') &&
        (data.patientId != null || data.patientName != null || data.requestedTime != null));
}
function appointmentDedupeKey(patientId, requestedTime) {
    const id = patientId.trim().toLowerCase();
    const raw = requestedTime.trim();
    if (!raw)
        return `${id}|`;
    const ms = Date.parse(raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`);
    const timeKey = Number.isNaN(ms) ? raw.toLowerCase() : new Date(ms).toISOString();
    return `${id}|${timeKey}`;
}
function extractScheduledAppointments(forms) {
    if (!forms?.length)
        return [];
    const appointments = [];
    const seen = new Set();
    for (const form of forms) {
        if (!isPsychPlusReferralResponse(form))
            continue;
        const data = form.formData ?? {};
        const patientId = pickFormString(data, 'patientId');
        const requestedTime = pickFormString(data, 'requestedTime');
        const dedupeKey = appointmentDedupeKey(patientId, requestedTime);
        if (seen.has(dedupeKey))
            continue;
        seen.add(dedupeKey);
        appointments.push({
            key: `appt-${form.id}`,
            patientId,
            contactStatus: pickFormString(data, 'contactStatus'),
            patientName: patientNameFromFormData(data),
            requestedTime,
            created: form.created,
        });
    }
    return appointments;
}
function ReadOnlySurvey({ form }) {
    const rootRef = useRef(null);
    const model = useMemo(() => {
        const m = new Model(form.survey);
        m.data = form.formData ?? {};
        m.mode = 'display';
        m.showNavigationButtons = false;
        m.showCompletedPage = false;
        m.showProgressBar = 'off';
        m.questionsOnPageMode = 'singlePage';
        bindQuillHtmlDisplay(m);
        return m;
    }, [form]);
    useLayoutEffect(() => {
        if (rootRef.current)
            applyQuillHtmlDisplay(model, rootRef.current);
    }, [model]);
    return (_jsx("div", { ref: rootRef, children: _jsx(Survey, { model: model }) }));
}
function FormThumbnail({ tile, onOpen }) {
    const fieldCount = answeredFieldCount(tile.form.formData);
    return (_jsxs("button", { type: "button", className: "form-tile", onClick: onOpen, children: [_jsx("span", { className: "form-tile-icon", "aria-hidden": "true", children: _jsxs("svg", { width: "28", height: "28", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.6", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("path", { d: "M14 2v6h6" }), _jsx("path", { d: "M8 13h8M8 17h6" })] }) }), _jsxs("span", { className: "form-tile-body", children: [_jsx("span", { className: "form-tile-title", children: tile.title }), _jsx("span", { className: "form-tile-source", children: tile.source }), _jsxs("span", { className: "form-tile-meta", children: [fieldCount, " ", fieldCount === 1 ? 'field' : 'fields', " completed"] })] })] }));
}
function configureDismissalSurvey(model) {
    model.completeText = 'Submit and dismiss';
    model.showCompletedPage = false;
    // Supporting-form JSON often sets showNavigationButtons to none, which hides Complete.
    model.showNavigationButtons = true;
    model.showCompleteButton = true;
}
function findDismissalFormData(detail) {
    const match = detail?.issueForms?.find((form) => form.formId === DISMISSAL_FORM_ID ||
        /dismissal/i.test(`${form.formTitle ?? ''} ${surveyTitle(form.survey)}`));
    const data = match?.formData;
    if (!data || Object.keys(data).length === 0)
        return null;
    return data;
}
function DismissalModal({ incidentId, existingFormData, submitting, error, onSubmit, onClose, }) {
    const surveyContainerRef = useRef(null);
    const onSubmitRef = useRef(onSubmit);
    const prefillRef = useRef(existingFormData);
    const inFlightRef = useRef(false);
    const [survey, setSurvey] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [loading, setLoading] = useState(true);
    const bindingKey = `dismiss-${incidentId}`;
    onSubmitRef.current = onSubmit;
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !submitting)
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, submitting]);
    useEffect(() => {
        if (!survey)
            return;
        survey.showCompleteButton = !submitting;
    }, [survey, submitting]);
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError('');
        inFlightRef.current = false;
        void (async () => {
            try {
                const definition = await fetchFormDefinition(String(DISMISSAL_FORM_ID));
                if (cancelled)
                    return;
                const model = new Model(definition.survey);
                configureDismissalSurvey(model);
                const prefill = prefillRef.current;
                if (prefill && Object.keys(prefill).length > 0) {
                    model.data = prefill;
                }
                model.onCompleting.add((_, options) => {
                    options.allowComplete = false;
                    if (inFlightRef.current)
                        return;
                    inFlightRef.current = true;
                    void (async () => {
                        try {
                            const faf = await executeValidateThenSubmit(bindingKey, model);
                            if (faf?.shouldStop) {
                                inFlightRef.current = false;
                                return;
                            }
                            const data = model.data && typeof model.data === 'object' && !Array.isArray(model.data)
                                ? model.data
                                : {};
                            await onSubmitRef.current(data);
                        }
                        catch {
                            inFlightRef.current = false;
                        }
                    })();
                });
                await bindSurvey({
                    formId: DISMISSAL_FORM_ID,
                    bindingKey,
                    survey: model,
                    host: {
                        formId: DISMISSAL_FORM_ID,
                        incidentId,
                        pageUrl: typeof window !== 'undefined' ? window.location.href : null,
                    },
                });
                configureDismissalSurvey(model);
                if (cancelled) {
                    resetFafBindings(bindingKey);
                    return;
                }
                setSurvey(model);
            }
            catch (err) {
                if (!cancelled) {
                    setLoadError(err instanceof Error ? err.message : 'Failed to load the dismissal form.');
                }
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            resetFafBindings(bindingKey);
        };
    }, [bindingKey, incidentId]);
    return (_jsx("div", { className: "form-modal-overlay", onClick: submitting ? undefined : onClose, role: "presentation", children: _jsxs("div", { className: "form-modal form-modal--dismissal", role: "dialog", "aria-modal": "true", "aria-label": DISMISSAL_FORM_TITLE, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "form-modal-header", children: [_jsxs("div", { children: [_jsx("h2", { children: "Dismissal" }), _jsx("span", { className: "form-modal-source", children: DISMISSAL_FORM_TITLE })] }), _jsx("button", { type: "button", className: "form-modal-close", onClick: onClose, "aria-label": "Close", disabled: submitting, children: "\u2715" })] }), _jsxs("div", { className: "form-modal-body", children: [loading && _jsx("div", { className: "table-empty", children: "Loading dismissal form\u2026" }), loadError && !loading && (_jsx("div", { className: "alert alert-error", role: "alert", children: loadError })), error && (_jsx("div", { className: "alert alert-error", role: "alert", children: error })), survey && !loading && (_jsxs("div", { ref: surveyContainerRef, className: "survey-container", children: [_jsx(Survey, { model: survey }), _jsx(TextareaMicOverlay, { containerRef: surveyContainerRef, survey: survey })] })), submitting && (_jsx("p", { className: "text-muted", children: "Attaching the dismissal form, creating a Data Sheet, and closing this L1\u2026" }))] }), _jsxs("div", { className: "form-modal-footer", children: [_jsx("button", { type: "button", className: "btn-ghost", disabled: submitting, onClick: onClose, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", disabled: !survey || submitting || loading, onClick: () => {
                                if (!survey || inFlightRef.current)
                                    return;
                                survey.tryComplete();
                            }, children: submitting ? 'Dismissing…' : 'Submit and dismiss' })] })] }) }));
}
function FormModal({ tile, onClose }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (_jsx("div", { className: "form-modal-overlay", onClick: onClose, role: "presentation", children: _jsxs("div", { className: "form-modal", role: "dialog", "aria-modal": "true", "aria-label": tile.title, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "form-modal-header", children: [_jsxs("div", { children: [_jsx("h2", { children: tile.title }), _jsx("span", { className: "form-modal-source", children: tile.source })] }), _jsx("button", { type: "button", className: "form-modal-close", onClick: onClose, "aria-label": "Close", children: "\u2715" })] }), _jsx("div", { className: "form-modal-body", children: _jsx(ReadOnlySurvey, { form: tile.form }) })] }) }));
}
function UpdateDescriptionBody({ text }) {
    if (!text)
        return _jsx("p", { className: "referral-update-description", children: "\u2014" });
    return (_jsx("p", { className: "referral-update-description", children: splitMentionParts(text).map((part, index) => part.type === 'mention' ? (_jsx("span", { className: "referral-update-mention", children: part.value }, index)) : (_jsx("span", { children: part.value }, index))) }));
}
function UpdateInspireModal({ description, error, submitting, processorName, people, title = 'Post Update', submitLabel = 'Post Update', placeholder = 'Enter an update for this L1… Type @ to tag a person or label (e.g. @important).', onDescriptionChange, onSubmit, onClose, }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !submitting)
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, submitting]);
    return (_jsx("div", { className: "form-modal-overlay update-inspire-overlay", onClick: submitting ? undefined : onClose, role: "presentation", children: _jsxs("div", { className: "form-modal update-inspire-modal", role: "dialog", "aria-modal": "true", "aria-label": title, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "form-modal-header", children: [_jsxs("div", { children: [_jsx("h2", { children: title }), processorName && (_jsxs("span", { className: "form-modal-source", children: ["From ", processorName] }))] }), _jsx("button", { type: "button", className: "form-modal-close", onClick: onClose, "aria-label": "Close", disabled: submitting, children: "\u2715" })] }), _jsxs("div", { className: "form-modal-body update-inspire-modal-body", children: [_jsx("label", { className: "detail-update-inspire-label", htmlFor: "update-inspire-description", children: "Update" }), _jsx(UpdateMentionTextarea, { id: "update-inspire-description", value: description, people: people, disabled: submitting, placeholder: placeholder, onChange: onDescriptionChange }), error && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: error })), _jsxs("div", { className: "detail-update-inspire-actions", children: [_jsx("button", { type: "button", className: "btn-ghost", disabled: submitting, onClick: onClose, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", disabled: submitting || !description.trim(), onClick: onSubmit, children: submitting ? 'Posting…' : submitLabel })] })] })] }) }));
}
function AddTaskModal({ assignees, assigneeId, description, dueDate, error, submitting, onAssigneeChange, onDescriptionChange, onDueDateChange, onSubmit, onClose, }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !submitting)
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, submitting]);
    return (_jsx("div", { className: "form-modal-overlay update-inspire-overlay", onClick: submitting ? undefined : onClose, role: "presentation", children: _jsxs("div", { className: "form-modal update-inspire-modal", role: "dialog", "aria-modal": "true", "aria-label": "Add Task", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "form-modal-header", children: [_jsx("div", { children: _jsx("h2", { children: "Add Task" }) }), _jsx("button", { type: "button", className: "form-modal-close", onClick: onClose, "aria-label": "Close", disabled: submitting, children: "\u2715" })] }), _jsxs("div", { className: "form-modal-body update-inspire-modal-body", children: [_jsx("label", { className: "detail-update-inspire-label", htmlFor: "add-task-assignee", children: "Assign to" }), _jsxs("select", { id: "add-task-assignee", className: "detail-task-select", value: assigneeId ?? '', disabled: submitting || assignees.length === 0, onChange: (event) => {
                                const next = Number(event.target.value);
                                onAssigneeChange(Number.isFinite(next) && next > 0 ? next : null);
                            }, children: [_jsx("option", { value: "", children: assignees.length === 0
                                        ? 'No eligible assignees on this L1'
                                        : 'Select an assignee…' }), assignees.map((assignee) => (_jsxs("option", { value: assignee.userId, children: [assignee.name, assignee.source === 'createdBy'
                                            ? ' (Created by)'
                                            : assignee.source === 'reportedBy'
                                                ? ' (Reported by)'
                                                : ''] }, assignee.userId)))] }), _jsx("label", { className: "detail-update-inspire-label", htmlFor: "add-task-description", children: "Task" }), _jsx("textarea", { id: "add-task-description", className: "detail-update-inspire-textarea", rows: 4, value: description, disabled: submitting, placeholder: "What should be done?", onChange: (event) => onDescriptionChange(event.target.value) }), _jsxs("label", { className: "detail-update-inspire-label", htmlFor: "add-task-due", children: ["Due date ", _jsx("span", { className: "text-muted", children: "(optional)" })] }), _jsx("input", { id: "add-task-due", className: "detail-task-select", type: "date", value: dueDate, disabled: submitting, onChange: (event) => onDueDateChange(event.target.value) }), error && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: error })), _jsxs("div", { className: "detail-update-inspire-actions", children: [_jsx("button", { type: "button", className: "btn-ghost", disabled: submitting, onClick: onClose, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", disabled: submitting || !description.trim() || assigneeId == null, onClick: onSubmit, children: submitting ? 'Adding…' : 'Add Task' })] })] })] }) }));
}
function CopyFromParentModal({ parentId, forms, attachments, selectedFormKeys, selectedAttachmentIds, loading, submitting, error, onToggleForm, onToggleAttachment, onSelectAllForms, onSelectAllAttachments, onSubmit, onClose, }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !submitting)
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, submitting]);
    const selectedCount = selectedFormKeys.size + selectedAttachmentIds.size;
    return (_jsx("div", { className: "form-modal-overlay update-inspire-overlay", onClick: submitting ? undefined : onClose, role: "presentation", children: _jsxs("div", { className: "form-modal update-inspire-modal copy-from-parent-modal", role: "dialog", "aria-modal": "true", "aria-label": "Copy from L1", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "form-modal-header", children: [_jsxs("div", { children: [_jsx("h2", { children: "Copy from  L1" }), _jsxs("p", { className: "copy-from-parent-subtitle", children: ["Choose forms and attachments from L1 ", parentId, " to add to this L1.5."] })] }), _jsx("button", { type: "button", className: "form-modal-close", onClick: onClose, "aria-label": "Close", disabled: submitting, children: "\u2715" })] }), _jsxs("div", { className: "form-modal-body update-inspire-modal-body", children: [loading ? (_jsx("p", { className: "text-muted", children: "Loading parent L1\u2026" })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "copy-from-parent-section", children: [_jsxs("div", { className: "copy-from-parent-section-head", children: [_jsx("h3", { children: "Forms" }), forms.length > 0 && (_jsx("button", { type: "button", className: "btn-ghost copy-from-parent-select-all", disabled: submitting, onClick: () => onSelectAllForms(selectedFormKeys.size < forms.length), children: selectedFormKeys.size < forms.length ? 'Select all' : 'Clear' }))] }), forms.length === 0 ? (_jsx("p", { className: "text-muted", children: "No completed forms on the parent L1." })) : (_jsx("ul", { className: "copy-from-parent-list", children: forms.map((form) => (_jsx("li", { children: _jsxs("label", { className: "copy-from-parent-option", children: [_jsx("input", { type: "checkbox", checked: selectedFormKeys.has(form.key), disabled: submitting, onChange: () => onToggleForm(form.key) }), _jsxs("span", { children: [_jsx("span", { className: "copy-from-parent-option-title", children: form.title }), _jsx("span", { className: "copy-from-parent-option-meta", children: form.source })] })] }) }, form.key))) }))] }), _jsxs("div", { className: "copy-from-parent-section", children: [_jsxs("div", { className: "copy-from-parent-section-head", children: [_jsx("h3", { children: "Attachments" }), attachments.length > 0 && (_jsx("button", { type: "button", className: "btn-ghost copy-from-parent-select-all", disabled: submitting, onClick: () => onSelectAllAttachments(selectedAttachmentIds.size < attachments.length), children: selectedAttachmentIds.size < attachments.length ? 'Select all' : 'Clear' }))] }), attachments.length === 0 ? (_jsx("p", { className: "text-muted", children: "No attachments on the parent L1." })) : (_jsx("ul", { className: "copy-from-parent-list", children: attachments.map((attachment) => {
                                                const sizeLabel = formatFileSize(attachment.size);
                                                return (_jsx("li", { children: _jsxs("label", { className: "copy-from-parent-option", children: [_jsx("input", { type: "checkbox", checked: selectedAttachmentIds.has(attachment.id), disabled: submitting, onChange: () => onToggleAttachment(attachment.id) }), _jsxs("span", { children: [_jsx("span", { className: "copy-from-parent-option-title", children: attachment.name }), sizeLabel ? (_jsx("span", { className: "copy-from-parent-option-meta", children: sizeLabel })) : null] })] }) }, attachment.id));
                                            }) }))] })] })), error && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: error })), _jsxs("div", { className: "detail-update-inspire-actions", children: [_jsx("button", { type: "button", className: "btn-ghost", disabled: submitting, onClick: onClose, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", disabled: submitting || loading || selectedCount === 0, onClick: onSubmit, children: submitting ? 'Copying…' : 'Copy selected' })] })] })] }) }));
}
export function ReferralDetailPage() {
    const { id } = useParams();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTile, setActiveTile] = useState(null);
    const [personTypes, setPersonTypes] = useState([]);
    const [personKinds, setPersonKinds] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [userId, setUserId] = useState(null);
    const [lockOpenL2, setLockOpenL2] = useState(false);
    const [processorName, setProcessorName] = useState('');
    const [newUpdateIds, setNewUpdateIds] = useState(() => new Set());
    const [downloadingId, setDownloadingId] = useState(null);
    const [downloadError, setDownloadError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');
    const fileInputRef = useRef(null);
    const [updateFormOpen, setUpdateFormOpen] = useState(false);
    const [updateDescription, setUpdateDescription] = useState('');
    const [updateSubmitting, setUpdateSubmitting] = useState(false);
    const [updateError, setUpdateError] = useState('');
    const [escalating, setEscalating] = useState(false);
    const [escalationError, setEscalationError] = useState('');
    const [escalationSuccess, setEscalationSuccess] = useState('');
    const [closing, setClosing] = useState(false);
    const [closeError, setCloseError] = useState('');
    const [closeSuccess, setCloseSuccess] = useState('');
    const [dismissalOpen, setDismissalOpen] = useState(false);
    const [dismissalPrefill, setDismissalPrefill] = useState(null);
    const [dismissing, setDismissing] = useState(false);
    const [dismissError, setDismissError] = useState('');
    const [dismissSuccess, setDismissSuccess] = useState('');
    const [personsEditing, setPersonsEditing] = useState(false);
    const [personDrafts, setPersonDrafts] = useState([]);
    const [personsSaving, setPersonsSaving] = useState(false);
    const [personsError, setPersonsError] = useState('');
    const [sites, setSites] = useState([]);
    const [siteUsers, setSiteUsers] = useState([]);
    const [siteEditing, setSiteEditing] = useState(false);
    const [siteDraft, setSiteDraft] = useState(null);
    const [siteSaving, setSiteSaving] = useState(false);
    const [siteError, setSiteError] = useState('');
    const [taskFormOpen, setTaskFormOpen] = useState(false);
    const [taskAssigneeId, setTaskAssigneeId] = useState(null);
    const [taskDescription, setTaskDescription] = useState('');
    const [taskDueDate, setTaskDueDate] = useState('');
    const [taskSubmitting, setTaskSubmitting] = useState(false);
    const [taskError, setTaskError] = useState('');
    const [taskNoteOpenId, setTaskNoteOpenId] = useState(null);
    const [taskNoteDescription, setTaskNoteDescription] = useState('');
    const [taskNoteSubmitting, setTaskNoteSubmitting] = useState(false);
    const [taskNoteError, setTaskNoteError] = useState('');
    const [taskBusyId, setTaskBusyId] = useState(null);
    const [copyFromParentOpen, setCopyFromParentOpen] = useState(false);
    const [parentCopyDetail, setParentCopyDetail] = useState(null);
    const [parentCopyLoading, setParentCopyLoading] = useState(false);
    const [selectedCopyFormKeys, setSelectedCopyFormKeys] = useState(() => new Set());
    const [selectedCopyAttachmentIds, setSelectedCopyAttachmentIds] = useState(() => new Set());
    const [copySubmitting, setCopySubmitting] = useState(false);
    const [copyError, setCopyError] = useState('');
    const [copySuccess, setCopySuccess] = useState('');
    useEffect(() => {
        ensureIncidentLookup()
            .then((lookup) => {
            setPersonTypes(lookup.personTypes);
            setPersonKinds(lookup.personKinds);
            setStatuses(lookup.statuses);
            setSites(lookup.sites);
            setSiteUsers(sortLookupUsers(lookup.users));
        })
            .catch((err) => console.error('[incidentLookup] failed', err));
    }, []);
    // Grouped by parent so schools sit under their district in the picker.
    const siteGroups = useMemo(() => {
        const groups = new Map();
        const sorted = [...sites].sort((a, b) => `${a.parentName ?? ''} ${a.name ?? ''}`.localeCompare(`${b.parentName ?? ''} ${b.name ?? ''}`));
        for (const site of sorted) {
            const key = site.parentName?.trim() || 'Top level';
            const existing = groups.get(key);
            if (existing) {
                existing.push(site);
            }
            else {
                groups.set(key, [site]);
            }
        }
        return [...groups.entries()];
    }, [sites]);
    const siteLabel = useMemo(() => {
        if (!detail)
            return '—';
        if (detail.siteId <= 0)
            return 'Not set';
        const match = sites.find((site) => site.id === detail.siteId);
        if (!match)
            return `Site ${detail.siteId}`;
        const name = match.name?.trim() || `Site ${match.id}`;
        const parent = match.parentName?.trim();
        return parent ? `${name} (${parent})` : name;
    }, [detail, sites]);
    useEffect(() => {
        let cancelled = false;
        ensureSignInUserInfo()
            .then((info) => {
            if (cancelled)
                return;
            if (typeof info.detail?.id === 'number') {
                setUserId(info.detail.id);
            }
            const roleName = info.detail?.roleName?.trim() ||
                (typeof info.detail?.role?.name === 'string' ? info.detail.role.name.trim() : '') ||
                '';
            setLockOpenL2(locksOpenL2(roleName));
            const first = typeof info.detail?.firstName === 'string' ? info.detail.firstName.trim() : '';
            const last = typeof info.detail?.lastName === 'string' ? info.detail.lastName.trim() : '';
            const full = [first, last].filter(Boolean).join(' ');
            setProcessorName(full || (typeof info.detail?.email === 'string' ? info.detail.email : ''));
        })
            .catch((err) => console.error('[signinUserInfo] failed', err));
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        const referralId = Number(id);
        if (!Number.isFinite(referralId)) {
            setError('Invalid L1 id.');
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError('');
        setNewUpdateIds(new Set());
        setUploadError('');
        setUploadSuccess('');
        fetchReferralDetail(referralId)
            .then((data) => {
            if (!cancelled)
                setDetail(data);
        })
            .catch((err) => {
            if (!cancelled)
                setError(err instanceof Error ? err.message : 'Failed to load L1.');
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [id]);
    const updateIdsKey = useMemo(() => (detail?.updatedInfos ?? []).map((u) => u.id).join(','), [detail?.updatedInfos]);
    useEffect(() => {
        if (!detail || userId == null)
            return;
        const updateIds = detail.updatedInfos.map((u) => u.id);
        const unseen = getUnseenReferralUpdateIds(userId, detail.id, updateIds);
        setNewUpdateIds(unseen);
        if (updateIds.length === 0)
            return;
        // Delay marking so the highlight sticks for this first view (and survives
        // React Strict Mode's immediate remount before the timeout fires).
        const timer = window.setTimeout(() => {
            markReferralUpdatesSeen(userId, detail.id, updateIds);
        }, 1000);
        return () => window.clearTimeout(timer);
    }, [detail, userId, updateIdsKey]);
    const personTypeLabel = (type) => namedValueLabel(personTypes, type, 'Type');
    async function handleAttachmentDownload(attachment) {
        const path = attachment.downloadUrl?.trim();
        if (!path) {
            setDownloadError(`No download path for ${attachment.name}.`);
            return;
        }
        setDownloadError('');
        setDownloadingId(attachment.id);
        try {
            if (/^https?:\/\//i.test(path)) {
                window.open(path, '_blank', 'noopener,noreferrer');
                return;
            }
            const file = await fetchAttachmentDownload(attachment.bucketName?.trim() || '', path, attachment.name);
            saveBase64File(file);
        }
        catch (err) {
            setDownloadError(err instanceof Error ? err.message : `Failed to download ${attachment.name}.`);
        }
        finally {
            setDownloadingId(null);
        }
    }
    async function handleAttachmentUpload(files) {
        if (!detail || detail.status !== OPEN_STATUS || !files?.length)
            return;
        const selected = Array.from(files);
        setUploadError('');
        setUploadSuccess('');
        setUploading(true);
        try {
            const outcomes = await Promise.allSettled(selected.map(async (file) => {
                if (isUploadTooLarge(file.size)) {
                    throw new Error(`${file.name}: ${UPLOAD_TOO_LARGE_MESSAGE}`);
                }
                const uploaded = await uploadBlobToS3(file, {
                    purpose: 'attachment',
                    fileName: file.name,
                    contentType: file.type,
                    caseId: detail.id,
                });
                return uploadReferralAttachment({
                    caseId: detail.id,
                    fileName: file.name,
                    key: uploaded.key,
                    size: uploaded.size,
                });
            }));
            const failed = outcomes.filter((o) => o.status === 'rejected').length;
            const uploaded = outcomes.length - failed;
            if (failed > 0) {
                const firstReject = outcomes.find((o) => o.status === 'rejected');
                const reason = firstReject?.reason instanceof Error
                    ? firstReject.reason.message
                    : 'Upload failed.';
                setUploadError(uploaded > 0
                    ? `Uploaded ${uploaded} of ${outcomes.length}; ${failed} failed. ${reason}`
                    : reason);
            }
            else {
                setUploadSuccess(uploaded === 1
                    ? '1 attachment uploaded.'
                    : `${uploaded} attachments uploaded.`);
            }
            const refreshed = await fetchReferralDetail(detail.id);
            setDetail(refreshed);
        }
        catch (err) {
            setUploadError(err instanceof Error ? err.message : 'Failed to upload attachment.');
        }
        finally {
            setUploading(false);
            if (fileInputRef.current)
                fileInputRef.current.value = '';
        }
    }
    async function handleUpdateInspireSubmit() {
        if (!detail)
            return;
        const description = updateDescription.trim();
        if (!description) {
            setUpdateError('Enter an update before submitting.');
            return;
        }
        const prefixed = prependReferralUpdateType(description);
        setUpdateError('');
        setUpdateSubmitting(true);
        try {
            await createReferralUpdatedInfo(detail.id, prefixed);
            setUpdateDescription('');
            setUpdateFormOpen(false);
            const refreshed = await fetchReferralDetail(detail.id);
            setDetail(refreshed);
        }
        catch (err) {
            setUpdateError(err instanceof Error ? err.message : 'Failed to submit update.');
        }
        finally {
            setUpdateSubmitting(false);
        }
    }
    const taskAssignees = useMemo(() => {
        if (!detail)
            return [];
        return collectTaskAssignees({
            personsInvolved: detail.personsInvolved,
            createdBy: detail.createdByUser
                ? {
                    id: detail.createdByUser.id ?? 0,
                    firstName: detail.createdByUser.firstName,
                    lastName: detail.createdByUser.lastName,
                    email: detail.createdByUser.email,
                }
                : null,
            reportBy: detail.reportByUser
                ? {
                    id: detail.reportByUser.id ?? 0,
                    firstName: detail.reportByUser.firstName,
                    lastName: detail.reportByUser.lastName,
                    email: detail.reportByUser.email,
                }
                : null,
            personKinds,
            users: siteUsers,
        });
    }, [detail, personKinds, siteUsers]);
    async function handleAddTaskSubmit() {
        if (!detail || detail.status === CLOSED_STATUS)
            return;
        const description = taskDescription.trim();
        if (!description) {
            setTaskError('Enter a task before submitting.');
            return;
        }
        if (taskAssigneeId == null) {
            setTaskError('Select an assignee.');
            return;
        }
        setTaskError('');
        setTaskSubmitting(true);
        try {
            const dueDate = taskDueDate.trim()
                ? new Date(`${taskDueDate}T17:00:00`).toISOString()
                : null;
            const refreshed = await createReferralTask(detail.id, taskAssigneeId, description, dueDate);
            setDetail(refreshed);
            setTaskDescription('');
            setTaskDueDate('');
            setTaskAssigneeId(null);
            setTaskFormOpen(false);
        }
        catch (err) {
            setTaskError(err instanceof Error ? err.message : 'Failed to add task.');
        }
        finally {
            setTaskSubmitting(false);
        }
    }
    async function handleTaskNoteSubmit() {
        if (!detail || taskNoteOpenId == null)
            return;
        if (detail.status === CLOSED_STATUS && detail.level !== 'L1' && detail.level !== 'L1.5' && detail.level != null) {
            return;
        }
        const description = taskNoteDescription.trim();
        if (!description) {
            setTaskNoteError('Enter an update before submitting.');
            return;
        }
        setTaskNoteError('');
        setTaskNoteSubmitting(true);
        try {
            const refreshed = await createReferralTaskUpdatedInfo(detail.id, taskNoteOpenId, description);
            setDetail(refreshed);
            setTaskNoteDescription('');
            setTaskNoteOpenId(null);
        }
        catch (err) {
            setTaskNoteError(err instanceof Error ? err.message : 'Failed to post task update.');
        }
        finally {
            setTaskNoteSubmitting(false);
        }
    }
    async function handleCompleteTask(task) {
        if (!detail)
            return;
        if (detail.status === CLOSED_STATUS && detail.level !== 'L1' && detail.level !== 'L1.5' && detail.level != null) {
            return;
        }
        const confirmed = window.confirm('Mark this task as done?');
        if (!confirmed)
            return;
        setTaskBusyId(task.id);
        setTaskError('');
        try {
            const refreshed = await completeReferralTask(detail.id, task.id);
            setDetail(refreshed);
        }
        catch (err) {
            setTaskError(err instanceof Error ? err.message : 'Failed to mark task as done.');
        }
        finally {
            setTaskBusyId(null);
        }
    }
    async function handleDeleteTask(task) {
        if (!detail || detail.status === CLOSED_STATUS)
            return;
        const confirmed = window.confirm('Delete this task? This cannot be undone.');
        if (!confirmed)
            return;
        setTaskBusyId(task.id);
        setTaskError('');
        try {
            const refreshed = await deleteReferralTask(detail.id, task.id);
            setDetail(refreshed);
        }
        catch (err) {
            setTaskError(err instanceof Error ? err.message : 'Failed to delete task.');
        }
        finally {
            setTaskBusyId(null);
        }
    }
    function beginPersonEditing() {
        if (!detail || detail.status === CLOSED_STATUS)
            return;
        setPersonDrafts(detail.personsInvolved.map((person) => {
            const draft = toPersonDraft(person);
            return {
                ...draft,
                type: normalizeDraftValue(draft.type, personTypes),
                kind: normalizeDraftValue(draft.kind, personKinds),
            };
        }));
        setPersonsError('');
        setPersonsEditing(true);
    }
    function addPersonDraft() {
        setPersonDrafts((current) => [
            ...current,
            {
                type: defaultLookupValue(personTypes),
                kind: defaultLookupValue(personKinds),
                firstName: '',
                lastName: '',
                email: '',
                program: null,
                additionalFields: null,
                sourceUserId: null,
            },
        ]);
    }
    function applySiteUserToPerson(index, user) {
        if (!user) {
            updatePersonDraft(index, {
                sourceUserId: null,
                additionalFields: withPersonUserId(personDrafts[index]?.additionalFields, null),
            });
            return;
        }
        updatePersonDraft(index, {
            sourceUserId: user.id,
            firstName: user.firstName?.trim() ?? '',
            lastName: user.lastName?.trim() ?? '',
            email: user.email?.trim() ?? '',
            additionalFields: withPersonUserId(personDrafts[index]?.additionalFields, user.id),
        });
    }
    function updatePersonDraft(index, patch) {
        setPersonDrafts((current) => current.map((person, personIndex) => personIndex === index ? { ...person, ...patch } : person));
    }
    async function savePersons() {
        if (!detail || personsSaving || detail.status === CLOSED_STATUS)
            return;
        const invalid = personDrafts.some((person) => !person.firstName.trim() && !person.lastName.trim());
        if (invalid) {
            setPersonsError('Enter a first or last name for every person.');
            return;
        }
        setPersonsSaving(true);
        setPersonsError('');
        try {
            const refreshed = await replaceReferralPersons(detail.id, personDrafts.map(({ sourceUserId, ...person }) => ({
                ...person,
                firstName: person.firstName.trim(),
                lastName: person.lastName.trim(),
                email: person.email.trim() || null,
                additionalFields: withPersonUserId(person.additionalFields, sourceUserId),
            })));
            setDetail(refreshed);
            setPersonsEditing(false);
            setPersonDrafts([]);
        }
        catch (err) {
            setPersonsError(err instanceof Error ? err.message : 'Failed to save people involved.');
        }
        finally {
            setPersonsSaving(false);
        }
    }
    function beginSiteEditing() {
        if (!detail || detail.status === CLOSED_STATUS)
            return;
        setSiteDraft(detail.siteId > 0 ? detail.siteId : null);
        setSiteError('');
        setSiteEditing(true);
    }
    async function saveSite() {
        if (!detail || siteSaving || detail.status === CLOSED_STATUS)
            return;
        if (siteDraft == null) {
            setSiteError('Choose a site.');
            return;
        }
        setSiteSaving(true);
        setSiteError('');
        try {
            const refreshed = await updateReferralSite(detail.id, siteDraft);
            setDetail(refreshed);
            setSiteEditing(false);
            setSiteDraft(null);
        }
        catch (err) {
            setSiteError(err instanceof Error ? err.message : 'Failed to update the site.');
        }
        finally {
            setSiteSaving(false);
        }
    }
    const hasRelatedEscalation = Boolean(detail?.children.some((child) => isEscalationLevel(child.level)));
    const dataSheetChildren = (detail?.children ?? []).filter(isDataSheetRelation);
    let isL1Detail = false;
    if (detail && !detail.parent) {
        isL1Detail = detail.level === 'L1' || detail.level == null;
    }
    const canRequestL15 = detail?.status === NEW_STATUS && isL1Detail && !hasRelatedEscalation;
    const canClose = Boolean(detail &&
        detail.status !== CLOSED_STATUS &&
        ((isL1Detail && !hasRelatedEscalation) || detail.level === 'L1.5'));
    const canDismiss = Boolean(detail && detail.status !== CLOSED_STATUS && isL1Detail && !hasRelatedEscalation);
    const canMutate = Boolean(detail && !isClosedIncidentStatus(detail.status));
    const canUpdateExistingTasks = Boolean(detail &&
        (canMutate || detail.level === 'L1' || detail.level === 'L1.5' || detail.level == null));
    const canCopyFromParent = Boolean(detail &&
        detail.level === 'L1.5' &&
        detail.parent &&
        canMutate &&
        !isClosedIncidentStatus(detail.status));
    const l1ActionsBusy = escalating || closing || dismissing || copySubmitting;
    const parentCopyForms = useMemo(() => (parentCopyDetail ? collectParentCopyForms(parentCopyDetail) : []), [parentCopyDetail]);
    async function openCopyFromParent() {
        if (!detail?.parent || l1ActionsBusy || !canCopyFromParent)
            return;
        setCopyFromParentOpen(true);
        setCopyError('');
        setCopySuccess('');
        setParentCopyDetail(null);
        setSelectedCopyFormKeys(new Set());
        setSelectedCopyAttachmentIds(new Set());
        setParentCopyLoading(true);
        try {
            const parent = await fetchReferralDetail(detail.parent.id);
            setParentCopyDetail(parent);
            const forms = collectParentCopyForms(parent);
            setSelectedCopyFormKeys(new Set(forms.map((form) => form.key)));
            setSelectedCopyAttachmentIds(new Set(parent.attachments.map((item) => item.id)));
        }
        catch (err) {
            setCopyError(err instanceof Error ? err.message : 'Failed to load the parent L1.');
        }
        finally {
            setParentCopyLoading(false);
        }
    }
    async function handleCopyFromParent() {
        if (!detail || copySubmitting || !canCopyFromParent)
            return;
        const formIds = parentCopyForms
            .filter((form) => selectedCopyFormKeys.has(form.key))
            .map((form) => form.id);
        const attachmentIds = [...selectedCopyAttachmentIds];
        if (formIds.length === 0 && attachmentIds.length === 0) {
            setCopyError('Select at least one form or attachment to copy.');
            return;
        }
        setCopySubmitting(true);
        setCopyError('');
        setCopySuccess('');
        try {
            const result = await copyReferralItemsFromParent({
                incidentId: detail.id,
                formIds,
                attachmentIds,
            });
            setDetail(result.detail);
            const copied = [
                result.copiedFormCount === 1
                    ? '1 form'
                    : result.copiedFormCount > 0
                        ? `${result.copiedFormCount} forms`
                        : '',
                result.copiedAttachmentCount === 1
                    ? '1 attachment'
                    : result.copiedAttachmentCount > 0
                        ? `${result.copiedAttachmentCount} attachments`
                        : '',
            ].filter(Boolean);
            if (result.errors.length > 0) {
                setCopyError(`${copied.length > 0 ? `Copied ${copied.join(' and ')}. ` : ''}${result.errors.join(' ')}`);
            }
            else {
                setCopyFromParentOpen(false);
                setCopySuccess(copied.length > 0
                    ? `Copied ${copied.join(' and ')} from the parent L1.`
                    : 'Nothing was copied.');
            }
        }
        catch (err) {
            setCopyError(err instanceof Error ? err.message : 'Failed to copy from the parent L1.');
        }
        finally {
            setCopySubmitting(false);
        }
    }
    async function handleRequestL15() {
        if (!detail || l1ActionsBusy || !canRequestL15)
            return;
        if (!window.confirm('Request L1.5? This creates an L1.5 child incident and locks this L1 for editing.')) {
            return;
        }
        setEscalating(true);
        setEscalationError('');
        setEscalationSuccess('');
        try {
            const refreshed = await requestL15(detail.id);
            setDetail(refreshed);
            setEscalationSuccess('L1.5 requested.');
        }
        catch (err) {
            setEscalationError(err instanceof Error ? err.message : 'Failed to request L1.5.');
        }
        finally {
            setEscalating(false);
        }
    }
    async function handleClose() {
        if (!detail || l1ActionsBusy || !canClose)
            return;
        const isL1 = detail.level === 'L1' || (detail.level == null && !detail.parent);
        const confirmed = window.confirm(isL1
            ? 'Close this L1? This creates a Data Sheet child incident and closes this L1.'
            : 'Close this L1.5?');
        if (!confirmed)
            return;
        setClosing(true);
        setCloseError('');
        setCloseSuccess('');
        try {
            const refreshed = await closeReferral(detail.id);
            setDetail(refreshed);
            setCloseSuccess(isL1 ? 'L1 closed. A Data Sheet was created.' : 'L1.5 closed.');
        }
        catch (err) {
            setCloseError(err instanceof Error ? err.message : 'Failed to close this incident.');
        }
        finally {
            setClosing(false);
        }
    }
    function openDismissal() {
        if (!detail || l1ActionsBusy || !canDismiss)
            return;
        setDismissError('');
        setDismissSuccess('');
        setDismissalPrefill(findDismissalFormData(detail));
        setDismissalOpen(true);
    }
    async function handleDismissSubmit(formData) {
        if (!detail || dismissing || !canDismiss)
            return;
        setDismissing(true);
        setDismissError('');
        setDismissSuccess('');
        try {
            const refreshed = await dismissReferral(detail.id, formData);
            setDetail(refreshed);
            setDismissalOpen(false);
            setDismissalPrefill(null);
            setDismissSuccess('L1 dismissed. A Data Sheet was created.');
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to dismiss this L1.';
            setDismissError(message);
            throw err instanceof Error ? err : new Error(message);
        }
        finally {
            setDismissing(false);
        }
    }
    const formTiles = useMemo(() => {
        if (!detail)
            return [];
        const tiles = [];
        detail.issueForms?.forEach((form) => {
            if (form.id <= 0)
                return;
            tiles.push({
                key: `issue-${form.id}`,
                title: form.formTitle || surveyTitle(form.survey) || `Form ${form.id}`,
                source: 'L1 Form',
                form,
            });
        });
        detail.tasks?.forEach((task) => {
            task.attachedForms?.forEach((form) => {
                if (form.id <= 0)
                    return;
                tiles.push({
                    key: `task-${task.id}-${form.id}`,
                    title: form.formTitle || surveyTitle(form.survey) || `Form ${form.id}`,
                    source: task.description || `Task ${task.id}`,
                    form,
                });
            });
        });
        return tiles.filter((tile) => answeredFieldCount(tile.form.formData ?? {}) > 0);
    }, [detail]);
    const scheduledAppointments = useMemo(() => extractScheduledAppointments(detail?.issueForms), [detail]);
    return (_jsxs("div", { className: "page", children: [_jsxs("div", { className: "page-header", children: [_jsxs("div", { children: [_jsx(Link, { to: "/", className: "back-link", children: "\u2190 Back to L1s" }), _jsxs("h1", { children: [detail?.level ?? (detail?.parent ? 'Child' : 'L1'), " ", id] }), detail && (_jsxs("p", { className: "page-subtitle", children: [detail.referId != null ? `Case ID ${detail.referId} · ` : '', "Created ", formatDateTime(detail.created), " by ", userName(detail.createdByUser)] }))] }), detail && (_jsxs("div", { className: "detail-header-actions", children: [_jsx("span", { className: `status-badge status-${detail.status}`, children: referralStatusLabel(detail.status, statuses) }), dataSheetChildren.map((child) => (_jsxs(Link, { to: `/referrals/${child.id}`, className: "btn-secondary detail-update-inspire-btn", children: ["Data Sheet #", child.id] }, child.id))), canRequestL15 && (_jsx("button", { type: "button", className: "btn-secondary detail-update-inspire-btn", disabled: l1ActionsBusy, onClick: () => void handleRequestL15(), children: escalating ? 'Requesting L1.5…' : 'Request L1.5' })), canCopyFromParent && (_jsx("button", { type: "button", className: "btn-secondary detail-update-inspire-btn", disabled: l1ActionsBusy, onClick: () => void openCopyFromParent(), children: "Copy from L1" })), canClose && (_jsx("button", { type: "button", className: "btn-secondary detail-update-inspire-btn", disabled: l1ActionsBusy, onClick: () => void handleClose(), children: closing ? 'Closing…' : 'Close' })), canDismiss && (_jsx("button", { type: "button", className: "btn-secondary detail-update-inspire-btn", disabled: l1ActionsBusy, onClick: openDismissal, children: dismissing ? 'Dismissing…' : 'Dismiss L1' })), detail.status === NEW_STATUS && isL1Detail && (_jsx(Link, { to: `/referrals/${detail.id}/edit`, className: "btn-secondary detail-update-inspire-btn", children: "Edit L1" })), detail.level === 'L1.5' && detail.parent && canMutate && (_jsx(Link, { to: `/referrals/${detail.id}/edit`, className: "btn-secondary detail-update-inspire-btn", children: "Edit L1.5" }))] }))] }), loading && _jsx("div", { className: "table-empty", children: "Loading L1\u2026" }), error && !loading && _jsx("div", { className: "table-empty", children: error }), escalationError && !loading && (_jsx("div", { className: "alert alert-error", role: "alert", children: escalationError })), escalationSuccess && !loading && (_jsx("div", { className: "alert alert-success", role: "status", children: escalationSuccess })), closeError && !loading && (_jsx("div", { className: "alert alert-error", role: "alert", children: closeError })), dismissError && !loading && !dismissalOpen && (_jsx("div", { className: "alert alert-error", role: "alert", children: dismissError })), dismissSuccess && !loading && (_jsxs("div", { className: "alert alert-success", role: "status", children: [dismissSuccess, dataSheetChildren.length > 0 && (_jsxs(_Fragment, { children: [' ', _jsxs(Link, { to: `/referrals/${dataSheetChildren[0].id}`, children: ["Open Data Sheet #", dataSheetChildren[0].id] })] }))] })), closeSuccess && !loading && (_jsxs("div", { className: "alert alert-success", role: "status", children: [closeSuccess, dataSheetChildren.length > 0 && (_jsxs(_Fragment, { children: [' ', _jsxs(Link, { to: `/referrals/${dataSheetChildren[0].id}`, children: ["Open Data Sheet #", dataSheetChildren[0].id] })] }))] })), copyError && !loading && !copyFromParentOpen && (_jsx("div", { className: "alert alert-error", role: "alert", children: copyError })), copySuccess && !loading && (_jsx("div", { className: "alert alert-success", role: "status", children: copySuccess })), detail && !loading && (_jsxs("div", { className: "referral-detail", children: [_jsxs("section", { className: "detail-card", children: [_jsx("h2", { children: "Summary" }), _jsxs("dl", { className: "detail-meta", children: [_jsxs("div", { children: [_jsx("dt", { children: "Case ID" }), _jsx("dd", { className: "mono", children: detail.referId != null ? detail.referId : '—' })] }), _jsxs("div", { children: [_jsxs("dt", { children: [detail.parent ? 'L2' : 'L1', " ID"] }), _jsx("dd", { className: "mono", children: detail.id })] }), _jsxs("div", { className: `detail-site-cell${siteEditing ? ' is-editing' : ''}`, children: [_jsx("dt", { children: "Site" }), _jsx("dd", { children: siteEditing ? (_jsxs("div", { className: "site-editor", children: [_jsxs("select", { "aria-label": "Site", value: siteDraft == null ? '' : String(siteDraft), disabled: siteSaving || sites.length === 0, onChange: (event) => {
                                                                const next = Number(event.target.value);
                                                                setSiteDraft(Number.isFinite(next) && next > 0 ? next : null);
                                                                setSiteError('');
                                                            }, children: [_jsx("option", { value: "", children: sites.length === 0 ? 'No sites available' : 'Select a site…' }), siteGroups.map(([parentName, groupSites]) => (_jsx("optgroup", { label: parentName, children: groupSites.map((site) => (_jsx("option", { value: site.id, children: site.name?.trim() || `Site ${site.id}` }, site.id))) }, parentName)))] }), siteError && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: siteError })), _jsxs("div", { className: "site-editor-actions", children: [_jsx("button", { type: "button", className: "btn-secondary", disabled: siteSaving, onClick: () => {
                                                                        setSiteEditing(false);
                                                                        setSiteError('');
                                                                    }, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", disabled: siteSaving, onClick: () => void saveSite(), children: siteSaving ? 'Saving…' : 'Save site' })] })] })) : (_jsxs("span", { className: "site-value", children: [_jsx("span", { className: detail.siteId > 0 ? undefined : 'site-missing', children: siteLabel }), canMutate && (_jsx("button", { type: "button", className: "btn-secondary site-change-btn", onClick: beginSiteEditing, children: detail.siteId > 0 ? 'Change' : 'Set site' }))] })) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Status" }), _jsx("dd", { children: referralStatusLabel(detail.status, statuses) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Incident type" }), _jsx("dd", { children: detail.issueType || '—' })] }), dataSheetChildren.length > 0 && (_jsxs("div", { children: [_jsx("dt", { children: "Data Sheet" }), _jsx("dd", { children: _jsx("span", { className: "detail-related-links", children: dataSheetChildren.map((child) => (_jsxs(Link, { to: `/referrals/${child.id}`, children: ["#", child.id] }, child.id))) }) })] })), _jsxs("div", { children: [_jsx("dt", { children: "Created" }), _jsx("dd", { children: formatDateTime(detail.created) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Last Updated" }), _jsx("dd", { children: formatDateTime(detail.updated) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Created By" }), _jsx("dd", { children: userName(detail.createdByUser) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Owner" }), _jsx("dd", { children: detail.ownerName ? (_jsx("span", { title: detail.ownerId != null ? `ID ${detail.ownerId}` : undefined, children: detail.ownerName })) : detail.ownerId != null ? (_jsxs("span", { className: "mono", children: ["#", detail.ownerId] })) : ('—') })] })] })] }), (detail.parent || detail.children.length > 0) && (_jsxs("section", { className: "detail-card", children: [_jsx("h2", { children: "Related incidents" }), _jsxs("div", { className: "related-incidents-list", children: [detail.parent && (_jsx(Link, { className: "related-incident-card", to: `/referrals/${detail.parent.id}`, children: relatedIncidentSummary(detail.parent, 'Parent', statuses) })), detail.children.map((child) => {
                                        const locked = lockOpenL2 && child.level === 'L2' && !isClosedIncidentStatus(child.status);
                                        const summary = relatedIncidentSummary(child, 'Child', statuses);
                                        if (locked) {
                                            return (_jsx("span", { className: "related-incident-card", title: "L2 details are available after this incident is closed.", children: summary }, child.id));
                                        }
                                        return (_jsx(Link, { className: "related-incident-card", to: `/referrals/${child.id}`, children: summary }, child.id));
                                    })] })] })), _jsxs("section", { className: "detail-card", children: [_jsx("h2", { children: "Scheduled appointments" }), scheduledAppointments.length === 0 ? (_jsx("p", { className: "text-muted", children: "No scheduled appointments found for this L1." })) : (_jsx("ul", { className: "scheduled-appointments-list", children: scheduledAppointments.map((appointment) => (_jsx("li", { className: "scheduled-appointment", children: _jsxs("dl", { className: "detail-meta", children: [_jsxs("div", { children: [_jsx("dt", { children: "PsychPlus Patient Id" }), _jsx("dd", { className: "mono", children: appointment.patientId || '—' })] }), _jsxs("div", { children: [_jsx("dt", { children: "Contact status" }), _jsx("dd", { children: appointment.contactStatus || '—' })] }), _jsxs("div", { children: [_jsx("dt", { children: "Patient name" }), _jsx("dd", { children: appointment.patientName || '—' })] }), _jsxs("div", { children: [_jsx("dt", { children: "Requested date/time" }), _jsx("dd", { children: formatDateTime(appointment.requestedTime) })] })] }) }, appointment.key))) }))] }), _jsxs("section", { className: "detail-card", children: [_jsxs("div", { className: "detail-card-heading", children: [_jsx("h2", { children: "People involved" }), !personsEditing && canMutate && (_jsx("button", { type: "button", className: "btn-secondary person-manage-btn", onClick: beginPersonEditing, children: "Manage people" }))] }), personsEditing ? (_jsxs("div", { className: "person-editor", children: [personDrafts.map((person, index) => (_jsxs("div", { className: "person-editor-row", children: [_jsx(SiteUserPicker, { className: "person-user-picker", users: siteUsers, selectedUserId: person.sourceUserId ?? null, onSelect: (user) => applySiteUserToPerson(index, user), disabled: personsSaving }), _jsxs("label", { children: ["First name", _jsx("input", { type: "text", value: person.firstName, onChange: (event) => updatePersonDraft(index, { firstName: event.target.value }) })] }), _jsxs("label", { children: ["Last name", _jsx("input", { type: "text", value: person.lastName, onChange: (event) => updatePersonDraft(index, { lastName: event.target.value }) })] }), _jsxs("label", { children: ["Type", _jsx("select", { value: person.type, onChange: (event) => updatePersonDraft(index, { type: Number(event.target.value) }), children: personTypes.map((type) => (_jsx("option", { value: type.value, children: type.name }, type.value))) })] }), _jsxs("label", { children: ["Kind", _jsx("select", { value: person.kind, onChange: (event) => updatePersonDraft(index, { kind: Number(event.target.value) }), children: personKinds.map((kind) => (_jsx("option", { value: kind.value, children: kind.name }, kind.value))) })] }), _jsxs("label", { children: ["Email ", _jsx("span", { className: "text-muted", children: "(optional)" }), _jsx("input", { type: "email", value: person.email, placeholder: "test@kokomo247.com", onChange: (event) => updatePersonDraft(index, { email: event.target.value }) })] }), _jsx("button", { type: "button", className: "person-remove-btn", "aria-label": `Remove ${person.firstName || person.lastName || 'person'}`, onClick: () => setPersonDrafts((current) => current.filter((_, personIndex) => personIndex !== index)), children: "Remove" })] }, person.id ?? `new-${index}`))), personDrafts.length === 0 && (_jsx("p", { className: "text-muted", children: "No people are currently linked." })), personsError && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: personsError })), _jsxs("div", { className: "person-editor-actions", children: [_jsx("button", { type: "button", className: "btn-secondary", disabled: personsSaving, onClick: addPersonDraft, children: "Add person" }), _jsxs("span", { className: "person-editor-actions-right", children: [_jsx("button", { type: "button", className: "btn-secondary", disabled: personsSaving, onClick: () => {
                                                            setPersonsEditing(false);
                                                            setPersonsError('');
                                                        }, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", disabled: personsSaving, onClick: () => void savePersons(), children: personsSaving ? 'Saving…' : 'Save people' })] })] })] })) : (detail.personsInvolved?.length ?? 0) === 0 ? (_jsx("p", { className: "text-muted", children: "No people linked to this incident." })) : (_jsx("ul", { className: "persons-involved-badges", children: detail.personsInvolved.map((person) => {
                                    const typeLabel = personTypeLabel(person.type);
                                    const isPatient = personTypes.some((entry) => entry.value === person.type &&
                                        entry.name.trim().toLowerCase() === 'patient');
                                    const badgeClass = `person-badge${isPatient ? ' is-patient' : ''}`;
                                    const body = (_jsxs(_Fragment, { children: [_jsx("span", { className: "person-badge-name", children: personDisplayName(person) }), _jsx("span", { className: "person-badge-type", children: typeLabel })] }));
                                    return (_jsx("li", { children: person.email ? (_jsx("a", { className: badgeClass, href: `mailto:${person.email}`, title: person.email, children: body })) : (_jsx("span", { className: badgeClass, title: "No email", children: body })) }, person.id));
                                }) }))] }), _jsxs("section", { className: "detail-card detail-card--tasks", children: [_jsx("div", { className: "detail-card-heading", children: _jsx("h2", { children: "Tasks" }) }), (detail.tasks?.length ?? 0) === 0 ? (_jsx("p", { className: "text-muted", children: "No tasks for this L1." })) : (_jsx("ul", { className: "referral-tasks-list", children: detail.tasks.map((task) => {
                                    const done = task.done || isReferralTaskDone(task);
                                    const canComplete = canUpdateExistingTasks && !done && userId != null && task.user?.id === userId;
                                    const busy = taskBusyId === task.id;
                                    return (_jsxs("li", { className: `referral-task${done ? ' is-done' : ''}`, children: [_jsxs("div", { className: "referral-task-header", children: [_jsxs("div", { children: [_jsx("p", { className: "referral-task-title", children: task.description }), _jsxs("p", { className: "referral-task-meta", children: [_jsxs("span", { children: ["Assigned to ", userName(task.user)] }), _jsx("span", { className: `referral-task-status${done ? ' is-done' : ''}`, children: done ? 'Done' : referralTaskStatusLabel(task.status) }), task.dueDate ? (_jsxs("span", { children: ["Due ", formatDateTime(task.dueDate)] })) : null, task.assignedBy && (task.assignedBy.firstName || task.assignedBy.lastName) ? (_jsxs("span", { children: ["From ", userName(task.assignedBy)] })) : null] })] }), _jsxs("div", { className: "referral-task-actions", children: [canComplete && (_jsx("button", { type: "button", className: "btn-secondary", disabled: busy, onClick: () => void handleCompleteTask(task), children: busy ? 'Saving…' : 'Mark as done' })), canUpdateExistingTasks && (_jsx("button", { type: "button", className: "btn-secondary", disabled: busy, onClick: () => {
                                                                    setTaskNoteOpenId(task.id);
                                                                    setTaskNoteDescription('');
                                                                    setTaskNoteError('');
                                                                }, children: "Add update" })), canMutate && (_jsx("button", { type: "button", className: "person-remove-btn", disabled: busy, onClick: () => void handleDeleteTask(task), children: "Delete" }))] })] }), task.message && task.message !== task.description ? (_jsx("p", { className: "referral-update-description", children: task.message })) : null, (task.updatedInfos?.length ?? 0) > 0 && (_jsx("ul", { className: "referral-task-updates", children: task.updatedInfos.map((info) => {
                                                    const parsed = parseTaskUpdateDescription(info.description);
                                                    const mentionTags = extractMentionTags(parsed.body);
                                                    return (_jsxs("li", { className: "referral-task-update", children: [_jsxs("div", { className: "referral-update-meta", children: [_jsxs("span", { className: "referral-update-meta-left", children: [parsed.done && (_jsx("span", { className: "referral-update-type", children: "Done" })), _jsx("time", { dateTime: info.created || undefined, children: formatDateTime(info.created) })] }), _jsx("span", { className: "referral-update-by", children: userName(info.createdByUser) })] }), mentionTags.length > 0 && (_jsx("ul", { className: "referral-update-tags", children: mentionTags.map((tag) => (_jsx("li", { className: "referral-update-tag", children: tag }, tag))) })), _jsx(UpdateDescriptionBody, { text: parsed.body })] }, info.id));
                                                }) }))] }, task.id));
                                }) })), taskError && !taskFormOpen && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: taskError })), canMutate && (_jsx("div", { className: "referral-updates-actions", children: _jsx("button", { type: "button", className: "referral-post-update-btn", onClick: () => {
                                        setTaskFormOpen(true);
                                        setTaskError('');
                                        setTaskAssigneeId(taskAssignees[0]?.userId ?? null);
                                    }, children: "Add Task" }) }))] }), _jsxs("section", { className: "detail-card detail-card--updates", children: [_jsxs("h2", { children: ["Updates", newUpdateIds.size > 0 && (_jsxs("span", { className: "referral-updates-new-count", children: [newUpdateIds.size, " new"] }))] }), (detail.updatedInfos?.length ?? 0) === 0 ? (_jsx("p", { className: "text-muted", children: "No updates for this L1." })) : (_jsx("ul", { className: "referral-updates-list", children: detail.updatedInfos.map((update) => {
                                    const isNew = newUpdateIds.has(update.id);
                                    const parsed = parseReferralUpdateDescription(update.description);
                                    const typeClass = parsed.type
                                        ? ` referral-update--${parsed.type.cssClass}`
                                        : '';
                                    const mentionTags = extractMentionTags(parsed.body);
                                    return (_jsxs("li", { className: `referral-update${typeClass}${isNew ? ' is-new' : ''}`, children: [_jsxs("div", { className: "referral-update-meta", children: [_jsxs("span", { className: "referral-update-meta-left", children: [parsed.type && (_jsx("span", { className: "referral-update-type", children: parsed.type.shortLabel })), _jsx("time", { dateTime: update.occured || update.created || undefined, children: formatDateTime(update.occured || update.created) })] }), _jsxs("span", { className: "referral-update-meta-right", children: [isNew && _jsx("span", { className: "referral-update-new-badge", children: "New" }), _jsx("span", { className: "referral-update-by", children: userName(update.createdByUser) })] })] }), mentionTags.length > 0 && (_jsx("ul", { className: "referral-update-tags", children: mentionTags.map((tag) => (_jsx("li", { className: "referral-update-tag", children: tag }, tag))) })), _jsx(UpdateDescriptionBody, { text: parsed.body })] }, update.id));
                                }) })), canMutate && (_jsx("div", { className: "referral-updates-actions", children: _jsx("button", { type: "button", className: "referral-post-update-btn", onClick: () => {
                                        setUpdateFormOpen(true);
                                        setUpdateError('');
                                    }, children: "Post Update" }) }))] }), _jsxs("section", { className: "detail-card", children: [_jsx("h2", { children: "Forms" }), formTiles.length === 0 ? (_jsx("p", { className: "text-muted", children: "No completed forms on this L1." })) : (_jsx("div", { className: "form-tile-grid", children: formTiles.map((tile) => (_jsx(FormThumbnail, { tile: tile, onOpen: () => setActiveTile(tile) }, tile.key))) }))] }), _jsxs("section", { className: "detail-card", children: [_jsxs("div", { className: "detail-card-heading", children: [_jsx("h2", { children: "Attachments" }), detail.status === OPEN_STATUS && (_jsxs("div", { className: "detail-attachment-upload", children: [_jsx("input", { ref: fileInputRef, type: "file", id: "detail-attachment-file", className: "detail-attachment-file-input", multiple: true, disabled: uploading, onChange: (event) => void handleAttachmentUpload(event.target.files) }), _jsx("button", { type: "button", className: "btn-secondary detail-attachment-upload-btn", disabled: uploading, onClick: () => fileInputRef.current?.click(), children: uploading ? 'Uploading…' : 'Add attachment' })] }))] }), uploadError && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: uploadError })), uploadSuccess && !uploadError && (_jsx("p", { className: "detail-attachment-success", role: "status", children: uploadSuccess })), downloadError && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: downloadError })), (detail.attachments?.length ?? 0) === 0 ? (_jsx("p", { className: "text-muted", children: detail.status === OPEN_STATUS
                                    ? 'No attachments yet. Add a file while this L1 is Received.'
                                    : 'No attachments on this L1.' })) : (_jsx("ul", { className: "detail-attachment-list", children: detail.attachments.map((attachment) => {
                                    const downloadable = canDownloadAttachment(attachment);
                                    const busy = downloadingId === attachment.id;
                                    const sizeLabel = formatFileSize(attachment.size);
                                    const meta = [sizeLabel, formatDateTime(attachment.created)]
                                        .filter((part) => part && part !== '—')
                                        .join(' · ');
                                    return (_jsxs("li", { className: "detail-attachment-item", children: [_jsx("span", { className: "detail-attachment-icon", "aria-hidden": "true", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.6", children: _jsx("path", { d: "M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" }) }) }), _jsxs("span", { className: "detail-attachment-body", children: [downloadable ? (_jsx("button", { type: "button", className: "detail-attachment-name detail-attachment-link", disabled: busy || uploading, onClick: () => void handleAttachmentDownload(attachment), children: busy ? `Downloading ${attachment.name}…` : attachment.name })) : (_jsx("span", { className: "detail-attachment-name", children: attachment.name })), meta && _jsx("span", { className: "detail-attachment-meta", children: meta }), downloadable ? (_jsx("button", { type: "button", className: "detail-attachment-download", disabled: busy || uploading, onClick: () => void handleAttachmentDownload(attachment), children: busy ? 'Downloading…' : 'Download' })) : (_jsx("span", { className: "detail-attachment-meta", children: "No download path" }))] })] }, attachment.id));
                                }) }))] })] })), activeTile && _jsx(FormModal, { tile: activeTile, onClose: () => setActiveTile(null) }), dismissalOpen && detail && (_jsx(DismissalModal, { incidentId: detail.id, existingFormData: dismissalPrefill, submitting: dismissing, error: dismissError, onSubmit: handleDismissSubmit, onClose: () => {
                    if (dismissing)
                        return;
                    setDismissalOpen(false);
                    setDismissError('');
                    setDismissalPrefill(null);
                } })), copyFromParentOpen && canCopyFromParent && detail?.parent && (_jsx(CopyFromParentModal, { parentId: detail.parent.id, forms: parentCopyForms, attachments: parentCopyDetail?.attachments ?? [], selectedFormKeys: selectedCopyFormKeys, selectedAttachmentIds: selectedCopyAttachmentIds, loading: parentCopyLoading, submitting: copySubmitting, error: copyError, onToggleForm: (key) => {
                    setSelectedCopyFormKeys((current) => {
                        const next = new Set(current);
                        if (next.has(key))
                            next.delete(key);
                        else
                            next.add(key);
                        return next;
                    });
                }, onToggleAttachment: (attachmentId) => {
                    setSelectedCopyAttachmentIds((current) => {
                        const next = new Set(current);
                        if (next.has(attachmentId))
                            next.delete(attachmentId);
                        else
                            next.add(attachmentId);
                        return next;
                    });
                }, onSelectAllForms: (selected) => {
                    setSelectedCopyFormKeys(selected ? new Set(parentCopyForms.map((form) => form.key)) : new Set());
                }, onSelectAllAttachments: (selected) => {
                    setSelectedCopyAttachmentIds(selected
                        ? new Set((parentCopyDetail?.attachments ?? []).map((item) => item.id))
                        : new Set());
                }, onSubmit: () => void handleCopyFromParent(), onClose: () => {
                    if (copySubmitting)
                        return;
                    setCopyFromParentOpen(false);
                    setCopyError('');
                } })), taskFormOpen && (_jsx(AddTaskModal, { assignees: taskAssignees, assigneeId: taskAssigneeId, description: taskDescription, dueDate: taskDueDate, error: taskError, submitting: taskSubmitting, onAssigneeChange: setTaskAssigneeId, onDescriptionChange: setTaskDescription, onDueDateChange: setTaskDueDate, onSubmit: () => void handleAddTaskSubmit(), onClose: () => {
                    if (taskSubmitting)
                        return;
                    setTaskFormOpen(false);
                    setTaskError('');
                } })), taskNoteOpenId != null && (_jsx(UpdateInspireModal, { title: "Task Update", submitLabel: "Post Update", placeholder: "Enter an update for this task\u2026 Type @ to tag a person or label (e.g. @important).", description: taskNoteDescription, error: taskNoteError, submitting: taskNoteSubmitting, processorName: processorName, people: detail?.personsInvolved ?? [], onDescriptionChange: setTaskNoteDescription, onSubmit: () => void handleTaskNoteSubmit(), onClose: () => {
                    if (taskNoteSubmitting)
                        return;
                    setTaskNoteOpenId(null);
                    setTaskNoteError('');
                } })), updateFormOpen && (_jsx(UpdateInspireModal, { description: updateDescription, error: updateError, submitting: updateSubmitting, processorName: processorName, people: detail?.personsInvolved ?? [], onDescriptionChange: setUpdateDescription, onSubmit: () => void handleUpdateInspireSubmit(), onClose: () => {
                    if (updateSubmitting)
                        return;
                    setUpdateFormOpen(false);
                    setUpdateError('');
                } }))] }));
}
